import type { MapMove, MovePhase } from '../types';
import type { MoveOverlaySet } from './types';
import { classifyMove } from '../constants';
import {
  PAST_COLORS,
  ACTIVE_COLOR,
  FUTURE_COLORS,
  ACTIVE_GLOW_COLOR,
  ACTIVE_DASH_COLOR,
} from '../constants';
import { decodePolyline } from '@utils/decodePolyline';

// TYPES --------------------------------------------------------------------- //

interface RouteOverlayResult {
  overlaySet: Partial<MoveOverlaySet>;
  routePath: google.maps.LatLngLiteral[];
  color: string;
  status: MovePhase;
}

interface CreateRouteOverlaysOptions {
  map: google.maps.Map;
  moves: MapMove[];
  prefersReducedMotion: boolean;
}

// FACTORY ------------------------------------------------------------------- //

/**
 * Creates the 3-layer route polylines (glow + main + animated dash) for each move.
 * Returns overlays, decoded paths, and color assignments for downstream use.
 * Manages `requestAnimationFrame` for dash animation; respects `prefers-reduced-motion`.
 */
export function createRouteOverlays({
  map,
  moves,
  prefersReducedMotion,
}: CreateRouteOverlaysOptions): {
  results: RouteOverlayResult[];
  animationFrameIds: number[];
  colorMap: Record<number, string>;
} {
  const results: RouteOverlayResult[] = [];
  const animationFrameIds: number[] = [];
  const colorMap: Record<number, string> = {};

  let pastIdx = 0;
  let futureIdx = 0;

  moves.forEach(move => {
    const status = classifyMove(move);
    let color: string;

    if (status === 'past') {
      color = PAST_COLORS[pastIdx % PAST_COLORS.length];
      pastIdx++;
    } else if (status === 'active') {
      color = ACTIVE_COLOR;
    } else {
      color = FUTURE_COLORS[futureIdx % FUTURE_COLORS.length];
      futureIdx++;
    }

    colorMap[move.id] = color;

    const overlaySet: Partial<MoveOverlaySet> = {
      moveId: move.id,
      status,
      routeGlow: null,
      routeMain: null,
      routeAnimated: null,
    };

    const polylineStr = move.lane?.route_polyline;
    let routePath: google.maps.LatLngLiteral[] = [];

    if (polylineStr) {
      routePath = decodePolyline(polylineStr);
    }

    // Fallback: straight line between pickup and delivery when no route polyline
    if (routePath.length < 2) {
      const pLat = move.lane?.pickup?.latitude;
      const pLng = move.lane?.pickup?.longitude;
      const dLat = move.lane?.delivery?.latitude;
      const dLng = move.lane?.delivery?.longitude;
      if (pLat != null && pLng != null && dLat != null && dLng != null) {
        routePath = [
          { lat: pLat, lng: pLng },
          { lat: dLat, lng: dLng },
        ];
      }
    }

    if (routePath.length >= 2) {
      const isFuture = status === 'future';

      // Glow polyline
      overlaySet.routeGlow = new google.maps.Polyline({
        path: routePath,
        geodesic: true,
        strokeColor: status === 'active' ? ACTIVE_GLOW_COLOR : color,
        strokeWeight: 10,
        strokeOpacity: isFuture ? 0.2 : 0.25,
        zIndex: 3,
        clickable: false,
        map,
      });

      // Main polyline
      overlaySet.routeMain = new google.maps.Polyline({
        path: routePath,
        geodesic: true,
        strokeColor: color,
        strokeWeight: 5,
        strokeOpacity: isFuture ? 0.8 : 1,
        zIndex: 4,
        clickable: false,
        map,
      });

      // Animated dashes (active moves only)
      if (status === 'active') {
        const lineSymbol: google.maps.Symbol = {
          path: 'M 0,-1 0,1',
          strokeOpacity: 1,
          strokeColor: ACTIVE_DASH_COLOR,
          strokeWeight: 4,
          scale: 3,
        };

        const DASH_REPEAT_PX = 24;
        const animatedPolyline = new google.maps.Polyline({
          path: routePath,
          strokeOpacity: 0,
          strokeColor: ACTIVE_COLOR,
          strokeWeight: 5,
          icons: [{ icon: lineSymbol, offset: '0', repeat: `${DASH_REPEAT_PX}px` }],
          zIndex: 5,
          clickable: false,
          map,
        });

        overlaySet.routeAnimated = animatedPolyline;

        if (!prefersReducedMotion) {
          let offset = 0;
          const animate = () => {
            offset = (offset + 0.6) % DASH_REPEAT_PX;
            animatedPolyline.set('icons', [
              { icon: lineSymbol, offset: `${offset}px`, repeat: `${DASH_REPEAT_PX}px` },
            ]);
            const frameId = requestAnimationFrame(animate);
            animationFrameIds.push(frameId);
          };
          const firstFrameId = requestAnimationFrame(animate);
          animationFrameIds.push(firstFrameId);
        }
      }
    }

    results.push({ overlaySet, routePath, color, status });
  });

  return { results, animationFrameIds, colorMap };
}
